import React, {useCallback, useEffect, useRef, useState} from "react";
import { createPortal } from "react-dom";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
    $getSelection,
    $isRangeSelection,
    SELECTION_CHANGE_COMMAND,
    COMMAND_PRIORITY_LOW, FORMAT_ELEMENT_COMMAND,
} from "lexical";
import { Button, Input, Space, Tooltip } from "antd";
import { IoIosSend } from "react-icons/io";
import { MdCancel } from "react-icons/md";
import {
    $getNodeByKey,
    $createTextNode,
    $createParagraphNode,
    $createRangeSelection,
    $setSelection,
    $isTextNode
} from "lexical";
import EditorToolbar from "./EditorToolbar";
import { $isHeadingNode, $createHeadingNode } from "@lexical/rich-text";
import {
    INDENT_CONTENT_COMMAND,
    OUTDENT_CONTENT_COMMAND,
} from "lexical";

function getSelectedHtmlString(editorRoot) {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return "";
    const range = selection.getRangeAt(0);
    if (range.collapsed) return "";
    if (!editorRoot.contains(range.commonAncestorContainer)) return "";
    const container = document.createElement("div");
    container.appendChild(range.cloneContents());
    return container.innerHTML.trim().replace(/\sdata-[^=]+="[^"]*"/g, "");
}

const HIDE_SELECTION_STYLE_ID = "ai-hide-native-selection-style";
const HIDE_SELECTION_CSS = `
  ::selection { background-color: transparent !important; color: inherit !important; }
  ::-moz-selection { background-color: transparent !important; color: inherit !important; }
`;

function installGlobalHideSelectionStyle() {
    if (document.getElementById(HIDE_SELECTION_STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = HIDE_SELECTION_STYLE_ID;
    style.textContent = HIDE_SELECTION_CSS;
    document.head.appendChild(style);
}
function removeGlobalHideSelectionStyle() {
    const el = document.getElementById(HIDE_SELECTION_STYLE_ID);
    if (el) el.remove();
}

function getAnchorRectFromSelection(sel) {
    if (!sel || sel.rangeCount === 0) return null;
    const r = sel.getRangeAt(0).cloneRange();
    r.collapse(true);
    return r.getBoundingClientRect();
}
function rangeIntersectsNode(range, node) {
    if (range.intersectsNode) {
        try {
            return range.intersectsNode(node);
        } catch {
            return false;
        }
    }
    const nodeRange = document.createRange();
    try {
        nodeRange.selectNode(node);
    } catch {
        return false;
    }
    return (
        range.compareBoundaryPoints(Range.START_TO_END, nodeRange) < 0 &&
        range.compareBoundaryPoints(Range.END_TO_START, nodeRange) > 0
    );
}
function getTextRectsInSelection(rootEl, selectionRange) {
    const rects = [];
    if (!rootEl || !selectionRange) return rects;
    const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
        if (rangeIntersectsNode(selectionRange, node)) {
            const r = document.createRange();
            const startIsThisNode = selectionRange.startContainer === node;
            const endIsThisNode = selectionRange.endContainer === node;
            const startOffset = startIsThisNode ? selectionRange.startOffset : 0;
            const endOffset = endIsThisNode ? selectionRange.endOffset : node.length;
            if (endOffset > startOffset) {
                r.setStart(node, startOffset);
                r.setEnd(node, endOffset);
                rects.push(...Array.from(r.getClientRects()).map((p) => ({
                    top: p.top + window.scrollY,
                    left: p.left + window.scrollX,
                    width: p.width,
                    height: p.height,
                })));
            }
        }
        node = walker.nextNode();
    }
    return rects;
}

export default function FloatingToolbar({ rewriteWithAI }) {
    const [editor] = useLexicalComposerContext();
    const savedSelectionRef = useRef(null);
    const overlayRef = useRef(null);
    const containerRef = useRef(null);
    const [coords, setCoords] = useState({ top: 0, left: 0, visible: false });
    const [showInput, setShowInput] = useState(false);
    const [selectedText, setSelectedText] = useState("");
    const [aiPrompt, setAiPrompt] = useState("");
    const [aiProcessing, setAiProcessing] = useState(false);
    const aiPromptRef = useRef(null);
    const insertedKeysRef = useRef([]);


    const setNativeSelectionHidden = useCallback(
        (hidden) => {
            const root = editor.getRootElement();
            if (!root) return;
            if (hidden) {
                root.classList.add("ai-overlay-hide-selection");
                installGlobalHideSelectionStyle();
            } else {
                root.classList.remove("ai-overlay-hide-selection");
                removeGlobalHideSelectionStyle();
            }
        },
        [editor]
    );

    function getScrollParents(el) {
        const parents = new Set();
        if (!el) {
            parents.add(window);
            return Array.from(parents);
        }

        const root = document.scrollingElement || document.documentElement;

        let node = el instanceof Element ? el : el.parentElement;
        while (node && node !== document.body && node !== document.documentElement) {
            if (node instanceof Element) {
                const style = getComputedStyle(node);
                const oy = style.overflowY;
                const ox = style.overflowX;
                if (/(auto|scroll|overlay)/.test(`${oy}${ox}`)) {
                    parents.add(node);
                }
            }
            node = node.parentElement;
        }

        parents.add(window);
        parents.add(root);

        return Array.from(parents);
    }



    useEffect(() => {
        const handleLayoutChange = () => {
            // Remove overlay highlight
            if (overlayRef.current) {
                overlayRef.current.fadeOutAndRemove?.();
                overlayRef.current = null;
            }

            // Hide floating toolbar
            setCoords({ top: 0, left: 0, visible: false });
            setShowInput(false);
            setAiPrompt("");
            savedSelectionRef.current = null;

            // Clear browser selection
            const sel = window.getSelection();
            if (sel && sel.rangeCount) sel.removeAllRanges();

            // Restore native selection appearance
            setNativeSelectionHidden(false);
        };

        // Alignment change
        const unregisterFormatListener = editor.registerCommand(
            FORMAT_ELEMENT_COMMAND,
            () => {
                handleLayoutChange();
                return false;
            },
            COMMAND_PRIORITY_LOW
        );

        // Indent
        const unregisterIndentListener = editor.registerCommand(
            INDENT_CONTENT_COMMAND,
            () => {
                handleLayoutChange();
                return false;
            },
            COMMAND_PRIORITY_LOW
        );

        // Outdent
        const unregisterOutdentListener = editor.registerCommand(
            OUTDENT_CONTENT_COMMAND,
            () => {
                handleLayoutChange();
                return false;
            },
            COMMAND_PRIORITY_LOW
        );

        // Window resize also counts as layout change
        window.addEventListener("resize", handleLayoutChange, { passive: true });

        return () => {
            unregisterFormatListener();
            unregisterIndentListener();
            unregisterOutdentListener();
            window.removeEventListener("resize", handleLayoutChange);
        };
    }, [editor, setNativeSelectionHidden]);


    useEffect(() => {
        const onAnyScroll = () => {
            if (overlayRef.current) {
                overlayRef.current.fadeOutAndRemove?.();
                overlayRef.current = null;
            }

            setCoords({ top: 0, left: 0, visible: false });
            setShowInput(false);
            setAiPrompt("");
            savedSelectionRef.current = null;

            const sel = window.getSelection();
            if (sel && sel.rangeCount) {
                sel.removeAllRanges();
            }

            setNativeSelectionHidden(false);
        };

        const root = editor.getRootElement();
        const targets = new Set(getScrollParents(root));
        targets.forEach(t => t && t.addEventListener("scroll", onAnyScroll, { passive: true }));

        window.addEventListener("wheel", onAnyScroll, { passive: true });
        window.addEventListener("touchmove", onAnyScroll, { passive: true });
        window.addEventListener("resize", onAnyScroll);

        return () => {
            targets.forEach(t => t && t.removeEventListener("scroll", onAnyScroll));
            window.removeEventListener("wheel", onAnyScroll);
            window.removeEventListener("touchmove", onAnyScroll);
            window.removeEventListener("resize", onAnyScroll);
        };
    }, [editor, setNativeSelectionHidden]);

    function ensureOverlay() {
        if (overlayRef.current) return overlayRef.current;
        const container = document.createElement("div");
        Object.assign(container.style, {
            position: "absolute",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            zIndex: "999999",
        });
        container.className = "ai-rewrite-overlay";
        document.body.appendChild(container);

        const blocks = [];

        function setBlocksFromRects(rects) {
            while (blocks.length < rects.length) {
                const el = document.createElement("div");
                el.className = "ai-rewrite-overlay-block";
                Object.assign(el.style, {
                    position: "absolute",
                    borderRadius: "4px",
                    background: "rgba(150, 200, 255, 0.4)",
                    transformOrigin: "top left",                 // 👈 crucial
                    willChange: "top, left, width, height, opacity, transform",
                });
                container.appendChild(el);
                blocks.push(el);
            }
            while (blocks.length > rects.length) {
                const el = blocks.pop();
                el.remove();
            }
            rects.forEach((r, i) => {
                const el = blocks[i];
                el.style.top = `${r.top}px`;
                el.style.left = `${r.left}px`;
                el.style.width = `${r.width}px`;
                el.style.height = `${r.height}px`;
                el.style.opacity = "1";
            });
        }

        function animateToNewRects(newRects) {
            if (!newRects || !newRects.length) return;

            const D = 1600;
            const E = "cubic-bezier(0.22, 1, 0.36, 1)";

            // ✨ Ensure we have a block per rect
            while (blocks.length < newRects.length) {
                const el = document.createElement("div");
                el.className = "ai-rewrite-overlay-block";
                Object.assign(el.style, {
                    position: "absolute",
                    borderRadius: "4px",
                    background: "rgba(150, 200, 255, 0.4)",
                    transformOrigin: "top left",
                    willChange: "top, left, width, height, opacity, transform",
                });
                container.appendChild(el);
                blocks.push(el);
            }
            while (blocks.length > newRects.length) {
                const el = blocks.pop();
                el.remove();
            }

            newRects.forEach((r, i) => {
                const el = blocks[i];
                const pad = 4;
                el.style.transition = [
                    `top 400ms ${E}`,
                    `left 400ms ${E}`,
                    `width 400ms ${E}`,
                    `height 400ms ${E}`,
                    `opacity 300ms ease ${i * 60}ms`,
                ].join(", ");

                el.style.top = `${r.top - pad}px`;
                el.style.left = `${r.left - pad}px`;
                el.style.width = `${r.width + pad * 2}px`;
                el.style.height = `${r.height + pad * 2}px`;
                el.style.opacity = "1";
                el.classList.remove("loading", "replacing", "magic-reveal");
                el.classList.add("magic-rewrite");
            });

            setTimeout(() => {
                blocks.forEach((b) => {
                    b.style.transition = "opacity 0.5s ease";
                    b.style.opacity = "0";
                });
            }, D);
        }

        function clearBlocks() {
            blocks.forEach((b) => (b.style.opacity = "0"));
        }

        function setMode(mode) {
            blocks.forEach((b) => {
                b.classList.remove("loading", "replacing");
                if (mode) b.classList.add(mode);
            });
        }

        function fadeOutAndRemove() {
            clearBlocks();
            container.style.transition = "opacity .2s ease";
            container.style.opacity = "0";
            setTimeout(() => container.remove(), 200);
        }

        overlayRef.current = {
            setBlocksFromRects,
            animateToNewRects,
            setMode,
            fadeOutAndRemove,
            container,
        };
        return overlayRef.current;
    }

    const hideUI = useCallback((clearNativeSelection = false) => {
        setShowInput(false);
        setAiPrompt("");
        savedSelectionRef.current = null;
        setCoords({ top: 0, left: 0, visible: false });
        overlayRef.current?.fadeOutAndRemove?.();
        overlayRef.current = null;
        setNativeSelectionHidden(false);

        if (clearNativeSelection) {
            const root = editor.getRootElement();
            const sel = window.getSelection();
            if (sel && sel.rangeCount && root && root.contains(sel.anchorNode)) {
                sel.removeAllRanges();
            }
        }
    }, [editor, setNativeSelectionHidden]);

    useEffect(() => {
        const root = editor.getRootElement();

        const handleSelectionChange = () => {
            const sel = window.getSelection();

            if (
                !sel ||
                sel.rangeCount === 0 ||
                !root ||
                !document.contains(root) ||
                !root.contains(sel.anchorNode) ||
                !root.contains(sel.focusNode)
            ) {
                return;
            }

            if (showInput) return;

            const range = sel.getRangeAt(0);
            let rects = getTextRectsInSelection(root, range);
            if (!rects.length) {
                hideUI(false);
                return;
            }

            setNativeSelectionHidden(true);

            const overlay = ensureOverlay();
            overlay.setBlocksFromRects(rects);

            const anchorRect = getAnchorRectFromSelection(sel) || range.getBoundingClientRect();
            setCoords({
                top: anchorRect.top + window.scrollY - 40,
                left: anchorRect.left + window.scrollX,
                visible: true,
            });

            editor.getEditorState().read(() => {
                const selection = $getSelection();
                if ($isRangeSelection(selection) && !selection.isCollapsed()) {
                    const html = getSelectedHtmlString(root);
                    setSelectedText(html);
                }
            });
        };

        document.addEventListener("selectionchange", handleSelectionChange);
        return () => document.removeEventListener("selectionchange", handleSelectionChange);
    }, [editor, showInput, hideUI, setNativeSelectionHidden]);

    useEffect(() => {
        const editorEl = editor.getRootElement();

        const onPointerDown = (e) => {
            const toolbarEl = containerRef.current;
            const inEditor = editorEl && editorEl.contains(e.target);
            const inToolbar = toolbarEl?.contains(e.target);

            if (!inEditor && !inToolbar) return;

            requestAnimationFrame(() => {
                editor.getEditorState().read(() => {
                    const selection = $getSelection();
                    if (!$isRangeSelection(selection) || selection.isCollapsed()) {
                        hideUI(false);
                    }
                });
            });
        };

        document.addEventListener("pointerdown", onPointerDown, true);
        return () => document.removeEventListener("pointerdown", onPointerDown, true);
    }, [editor, showInput, hideUI]);

    useEffect(() => {
        return editor.registerCommand(
            SELECTION_CHANGE_COMMAND,
            () => {
                if (showInput) return false;
                editor.getEditorState().read(() => {
                    const selection = $getSelection();
                    if (!$isRangeSelection(selection) || selection.isCollapsed()) {
                        hideUI(false);
                    }
                });
                return false;
            },
            COMMAND_PRIORITY_LOW
        );
    }, [editor, showInput, hideUI]);

    useEffect(() => {
        if (showInput && aiPromptRef.current) aiPromptRef.current.focus();
    }, [showInput]);


    function getTextRectsForInsertedNodes(editor, keys) {
        const rects = [];
        if (!keys || !keys.length) return rects;

        for (const key of keys) {
            const el = editor.getElementByKey(key);
            if (!el) continue;

            const walker = document.createTreeWalker(
                el,
                NodeFilter.SHOW_TEXT,
                {
                    acceptNode(node) {
                        return node.nodeValue && node.nodeValue.trim().length
                            ? NodeFilter.FILTER_ACCEPT
                            : NodeFilter.FILTER_REJECT;
                    },
                },
                false
            );

            let tn = walker.nextNode();
            while (tn) {
                try {
                    const r = document.createRange();
                    r.setStart(tn, 0);
                    r.setEnd(tn, tn.length);

                    const pieces = Array.from(r.getClientRects());
                    for (const p of pieces) {
                        rects.push({
                            top: p.top + window.scrollY,
                            left: p.left + window.scrollX,
                            width: p.width,
                            height: p.height,
                        });
                    }

                } catch {} // skip on any DOM edge case
                tn = walker.nextNode();
            }
        }

        return rects;
    }

    const setOverlayMode = (mode) => overlayRef.current?.setMode(mode);

    const applyReplacement = (newText) => {
        const savedSel = savedSelectionRef.current;
        if (!savedSel) return;

        editor.update(() => {
            const { anchorKey, anchorOffset, focusKey, focusOffset } = savedSel;
            const rangeSelection = $createRangeSelection();

            const anchorNode = $getNodeByKey(anchorKey);
            const focusNode = $getNodeByKey(focusKey);
            const anchorType = $isTextNode(anchorNode) ? "text" : "element";
            const focusType = $isTextNode(focusNode) ? "text" : "element";

            rangeSelection.anchor.set(anchorKey, anchorOffset, anchorType);
            rangeSelection.focus.set(focusKey, focusOffset, focusType);
            $setSelection(rangeSelection);

            const selection = $getSelection();
            if (!$isRangeSelection(selection)) return;

            const top = selection.anchor.getNode().getTopLevelElementOrThrow();
            const isHeading = $isHeadingNode(top);
            const headingTag = isHeading ? top.getTag() : null;

            selection.removeText();

            const lines = newText.split("\n");
            const containers = [];
            const insertedTextNodes = [];

            for (const line of lines) {
                const container = isHeading ? $createHeadingNode(headingTag) : $createParagraphNode();
                if (line.trim().length) {
                    const tn = $createTextNode(line);
                    container.append(tn);
                    insertedTextNodes.push(tn);
                }
                containers.push(container);
            }

            selection.insertNodes(containers);

            if (insertedTextNodes.length) {
                const first = insertedTextNodes[0];
                const last = insertedTextNodes[insertedTextNodes.length - 1];
                const sel2 = $createRangeSelection();
                sel2.anchor.set(first.getKey(), 0, "text");
                sel2.focus.set(last.getKey(), last.getTextContentSize(), "text");
                $setSelection(sel2);
            }

            insertedKeysRef.current = insertedTextNodes.map((n) => n.getKey());
        });
    };

    const startProcessing = async () => {
        setAiProcessing(true);

        try {
            if (!overlayRef.current) {
                const sel = window.getSelection();
                if (sel && sel.rangeCount) {
                    const root = editor.getRootElement();
                    const rects = getTextRectsInSelection(root, sel.getRangeAt(0));
                    if (rects.length) ensureOverlay().setBlocksFromRects(rects);
                }
            }

            setOverlayMode("loading");

            const newText = await rewriteWithAI(selectedText, aiPrompt);
            if (typeof newText === "string") {

                if(selectedText !== newText) {
                    await new Promise((r) => setTimeout(r, 400));

                    applyReplacement(newText);

                    await new Promise((r) => {
                        requestAnimationFrame(() => {
                            requestAnimationFrame(r);
                        });
                    });

                    let newRects = [];

                    if (insertedKeysRef.current && insertedKeysRef.current[0] instanceof Range) {
                        const newRange = insertedKeysRef.current[0];
                        newRects = Array.from(newRange.getClientRects()).map(r => ({
                            top: r.top + window.scrollY,
                            left: r.left + window.scrollX,
                            width: r.width,
                            height: r.height,
                        }));
                    } else {
                        newRects = getTextRectsForInsertedNodes(editor, insertedKeysRef.current);
                    }

                    if (!newRects.length) {
                        const root = editor.getRootElement();
                        const sel = window.getSelection();
                        if (sel && sel.rangeCount) {
                            newRects = getTextRectsInSelection(root, sel.getRangeAt(0));
                        }
                    }

                    await new Promise((r) => {
                        requestAnimationFrame(() => {
                            requestAnimationFrame(r);
                        });
                    });

                    const root = editor.getRootElement();
                    const sel = window.getSelection();

                    if (root && sel && sel.rangeCount) {
                        const range = sel.getRangeAt(0);
                        newRects = getTextRectsInSelection(root, range);
                    }

                    if (!newRects.length && insertedKeysRef.current?.length) {
                        newRects = getTextRectsForInsertedNodes(editor, insertedKeysRef.current);
                    }

                    if (newRects.length) {
                        await new Promise((r) => setTimeout(r, 0));
                        overlayRef.current?.animateToNewRects(newRects);
                    }

                    overlayRef.current?.setMode("replacing");
                    await new Promise((r) => setTimeout(r, 1300));

                    overlayRef.current?.fadeOutAndRemove();
                    overlayRef.current = null;

                    setShowInput(false);
                    setCoords({ top: 0, left: 0, visible: false });

                    if (sel) {
                        if (sel.removeAllRanges) {
                            sel.removeAllRanges();
                        } else if (sel.empty) {
                            sel.empty();
                        }
                    }
                } else {
                    overlayRef.current?.fadeOutAndRemove();
                    overlayRef.current = null;

                    setShowInput(false);
                    setCoords({ top: 0, left: 0, visible: false });
                }
            }
        } finally {
            hideUI(false);
            setAiProcessing(false);
        }
    };

    const baseStyle = {
        position: "absolute",
        top: coords.top,
        left: coords.left,
        transform: "translateX(-5%)",
        background: "white",
        borderRadius: "6px",
        marginTop: "-5px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
        zIndex: 1000,
    };

    return createPortal(
        <div ref={containerRef}>
            {showInput ? (
                <Space.Compact style={{ ...baseStyle, width: 320 }}>
                    <Input
                        value={aiPrompt}
                        ref={aiPromptRef}
                        onKeyDown={async (e) => {
                            if (e.key === "Enter") await startProcessing();
                            else if (e.key === "Escape") hideUI(false);
                        }}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        placeholder="Describe how to rewrite..."
                    />
                    <Tooltip title="Rewrite with AI">
                        <Button
                            loading={aiProcessing}
                            icon={<IoIosSend />}
                            onClick={startProcessing}
                        />
                    </Tooltip>
                    <Tooltip title="Cancel">
                        <Button
                            disabled={aiProcessing}
                            icon={<MdCancel />}
                            onClick={() => hideUI(false)}
                        />
                    </Tooltip>
                </Space.Compact>
            ) : coords.visible ? (
                <div style={baseStyle}>
                    <EditorToolbar
                        showMagicRewrite
                        showViewSource={false}
                        miniToolbar={true}
                        selectedText={() => {
                            editor.update(() => {
                                const selection = $getSelection();
                                if ($isRangeSelection(selection)) {
                                    savedSelectionRef.current = {
                                        anchorKey: selection.anchor.key,
                                        anchorOffset: selection.anchor.offset,
                                        focusKey: selection.focus.key,
                                        focusOffset: selection.focus.offset,
                                    };
                                    const html = getSelectedHtmlString(editor.getRootElement());
                                    if (html) setSelectedText(html);
                                }
                            });
                            setShowInput(true);
                        }}
                    />
                </div>
            ) : null}
        </div>,
        document.body
    );
}
